// src/services/contentGapsService.js
//
// Reads the content backlog and attaches a finished topic to every state that
// needs it.
//
// WHERE THE DATA COMES FROM
// -------------------------
// `audit-standards-coverage.mjs --all --write-gaps` computes the backlog and
// stores it in contentGaps/{subject}_{grade}. Working it out live would mean
// reading every standards document and every layout — about 1,200 reads before
// anything could be drawn, billed on every page load. Precomputing makes it 27
// reads, at the cost of the list going stale between audit runs. The panel
// shows the generated date so nobody mistakes a week-old number for today's.
//
// SCALE
// -----
// 19,622 distinct gaps at the last run, of which 1,660 are needed by more than
// one state. The long tail is single-state social studies, which no shared
// song can fix. The panel leads with multi-state gaps for that reason; the
// tail is searchable rather than scrollable.
//
// ATTACHING
// ---------
// markComplete() writes a topic onto every state page that needs it, labelled
// with THAT state's code — the gap stores the per-state original, so Ohio gets
// K.PS.2 and West Virginia gets S.K.10 from the same click. It backs up each
// layout first and refuses outright if the topic is not in topicLibrary,
// because a typo would otherwise scatter empty tiles across sixty pages.

import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";

const VIEWS = ["student", "educator"];

/**
 * Loads ONLY the three summary documents: _meta, _multi and _themes.
 *
 * Reading all 27 gap documents to draw 120 rows meant pulling roughly 7MB
 * before anything appeared, with no progress indicator — the panel simply sat
 * on "Loading" long enough to look broken. The multi-state view is what opens
 * by default and _multi serves it in one small read; per-grade documents load
 * on demand in loadGradeGaps() below.
 */
export async function loadSummary() {
  const [meta, multi, themes] = await Promise.all([
    getDoc(doc(db, "contentGaps", "_meta")),
    getDoc(doc(db, "contentGaps", "_multi")),
    getDoc(doc(db, "contentGaps", "_themes")),
  ]);

  if (!meta.exists() && !multi.exists()) return null;

  return {
    // Distinguishes "the audit has not written _multi yet" from "there are no
    // gaps". The panel said "nothing matches those filters" for the first,
    // which sent someone looking at filters when the document was missing.
    hasMulti: multi.exists(),
    generatedAt: meta.exists() ? meta.data().generatedAt : null,
    total: meta.exists() ? meta.data().totalGaps : 0,
    multiState: meta.exists() ? meta.data().multiStateGaps : 0,
    gaps: multi.exists() ? multi.data().gaps || [] : [],
    truncated: multi.exists() ? Boolean(multi.data().truncated) : false,
    themes: themes.exists() ? themes.data().themes || [] : [],
  };
}

/**
 * One subject and grade at a time — a few hundred rows rather than 19,000.
 *
 * The full backlog is only reachable this way on purpose. Someone filling
 * Georgia's 4th grade social studies wants those 76 standards, not everything.
 */
export async function loadGradeGaps(subject, grade) {
  const id = `${subject}_${grade}`;
  const snap = await getDoc(doc(db, "contentGaps", id));
  if (!snap.exists()) return [];
  return (snap.data().gaps || []).map((g) => ({ ...g, docId: id }));
}

/**
 * Saves the slug someone typed against a gap, without touching anything else.
 *
 * Stored on the gap rather than held in component state so it survives a
 * reload and, more importantly, survives the next audit run — the writer
 * preserves existing suggestedSlug values precisely so this is not lost.
 */
export async function saveSuggestedSlug(docId, code, slug) {
  // Written to BOTH the per-grade document and _multi. They are two copies of
  // the same gap, and a slug that only landed in one would vanish the moment
  // the panel switched views.
  for (const id of [docId, "_multi"]) {
    const ref = doc(db, "contentGaps", id);
    const snap = await getDoc(ref);
    if (!snap.exists()) continue;
    const gaps = (snap.data().gaps || []).map((g) =>
      g.code === code ? { ...g, suggestedSlug: slug } : g
    );
    await updateDoc(ref, { gaps });
  }
}

/**
 * Puts a finished topic on every page in `states`.
 *
 * onProgress is called after each page so a sixty-page attach can show
 * movement instead of appearing frozen.
 *
 * Returns { added, already, missing } — `missing` being states whose layout
 * does not exist yet, which is information rather than an error: the topic
 * lands there when the layout is seeded.
 */
export async function markComplete(gap, slug, onProgress) {
  const topicSnap = await getDoc(doc(db, "topicLibrary", slug));
  if (!topicSnap.exists()) {
    throw new Error(
      `No topic "${slug}" in the library. Add it in Topic Library first — ` +
        "attaching a slug that does not exist would put empty tiles on every page."
    );
  }
  const topic = topicSnap.data();
  const stamp = new Date().toISOString().replace(/[:.]/g, "-");

  let added = 0, already = 0, missing = 0;
  const total = gap.states.length * VIEWS.length;
  let done = 0;

  for (const entry of gap.states) {
    for (const view of VIEWS) {
      const id = `${entry.state}_${gap.grade}_${gap.subject}_${view}`;
      const ref = doc(db, "portalLayouts", id);
      const snap = await getDoc(ref);
      done += 1;
      if (onProgress) onProgress(done, total, id);

      if (!snap.exists()) { if (view === "student") missing += 1; continue; }

      const data = snap.data();
      const topics = [...(data.topics || [])];
      if (topics.some((t) => t.slug === slug)) {
        if (view === "student") already += 1;
        continue;
      }

      topics.push({
        slug,
        title: topic.title || slug,
        // The state's own code, from the gap. This is the whole point: one
        // click, and every state shows the number it actually publishes.
        standardNumber: entry.code,
        standardNumbers: [entry.code],
        description: gap.description || "",
        gameType: topic.gameType || "multipleChoice",
        titleImage: topic.titleImage || "",
        lyrics: topic.lyrics || [],
        videoUrl: topic.videoUrl || "",
        musicUrl: topic.musicUrl || "",
        visible: true,
      });

      // Snapshot before writing, same convention as the scripts.
      await setDoc(doc(db, "layoutBackups", `${id}__${stamp}`), data);
      await updateDoc(ref, { topics: topics.map((t, i) => ({ ...t, order: i })) });
      if (view === "student") added += 1;
    }
  }

  // Mark the gap done, in both copies, so the panel can drop it from the list
  // without waiting for the next audit run. The audit is a snapshot; making a
  // completed row sit there looking undone until somebody re-runs it would
  // teach people to ignore the list.
  await markGapCompleted(gap, slug, added);

  // Record the shared standard on the topic itself.
  //
  // topicLibrary held content only — title, video, song — and the standard
  // lived on each layout entry. That meant a topic recorded today was invisible
  // to seed-state-science.mjs, which asks the library "which topics teach
  // K-PS3-2?". A song attached to thirty states still reported as "no topic in
  // library" for the thirty-first, and the next state seeded silently missed
  // it. Writing the shared code here closes that loop.
  await recordStandardOnTopic(slug, gap.code);

  // Fold the new states into topicUsage/_all rather than making someone re-run
  // audit-topic-usage.mjs after every attach. We know exactly which states were
  // just written, so recomputing from 1,148 layouts to learn what we already
  // know would be work for nothing.
  await recordUsage(gap, slug);

  return { added, already, missing, stamp };
}

/**
 * Adds a shared standard code to the topic, without disturbing the others.
 *
 * Union, never replace: a topic legitimately teaches several standards, and a
 * second attach must not wipe the first.
 */
async function recordStandardOnTopic(slug, code) {
  const ref = doc(db, "topicLibrary", slug);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;

  const data = snap.data();
  const existing = new Set([
    ...(Array.isArray(data.standardNumbers) ? data.standardNumbers : []),
    ...(data.standardNumber ? [data.standardNumber] : []),
  ]);
  if (existing.has(code)) return;
  existing.add(code);

  const list = [...existing].sort();
  await updateDoc(ref, {
    standardNumbers: list,
    // Kept populated for anything still reading the original single field.
    standardNumber: data.standardNumber || list[0],
  });
}

/** Records the completion on the gap in both the grade document and _multi. */
async function markGapCompleted(gap, slug, added) {
  const patch = {
    completedSlug: slug,
    completedAt: new Date().toISOString(),
    completedPages: added,
  };
  for (const id of [gap.docId, "_multi"]) {
    const ref = doc(db, "contentGaps", id);
    const snap = await getDoc(ref);
    if (!snap.exists()) continue;
    const gaps = (snap.data().gaps || []).map((g) =>
      g.code === gap.code && g.grade === gap.grade && g.subject === gap.subject
        ? { ...g, ...patch }
        : g
    );
    await updateDoc(ref, { gaps });
  }
}

/** Clears the completion flag, for when something was marked done by mistake. */
export async function unmarkGap(gap) {
  for (const id of [gap.docId, "_multi"]) {
    const ref = doc(db, "contentGaps", id);
    const snap = await getDoc(ref);
    if (!snap.exists()) continue;
    const gaps = (snap.data().gaps || []).map((g) =>
      g.code === gap.code && g.grade === gap.grade && g.subject === gap.subject
        ? { ...g, completedSlug: "", completedAt: "", completedPages: 0 }
        : g
    );
    await updateDoc(ref, { gaps });
  }
}

/**
 * Merges the states just attached into topicUsage/_all.
 *
 * Only ever adds. If the document does not exist yet the script has never run,
 * and inventing a partial one would make the Topic Library show counts that
 * are confidently wrong — better it keeps showing nothing until the real count
 * is generated.
 */
async function recordUsage(gap, slug) {
  const ref = doc(db, "topicUsage", "_all");
  const snap = await getDoc(ref);
  if (!snap.exists()) return;

  const data = snap.data();
  const usage = { ...(data.usage || {}) };
  const entry = usage[slug] || { stateCount: 0, states: [] };
  const byState = new Map(entry.states.map((s) => [s.state, s]));

  for (const st of gap.states) {
    const existing = byState.get(st.state);
    if (existing) {
      const grades = new Set([...(existing.grades || []), gap.grade]);
      const subjects = new Set([...(existing.subjects || []), gap.subject]);
      byState.set(st.state, {
        ...existing,
        grades: [...grades].sort(),
        subjects: [...subjects].sort(),
      });
    } else {
      byState.set(st.state, {
        state: st.state,
        stateName: st.stateName || st.state.toUpperCase(),
        grades: [gap.grade],
        subjects: [gap.subject],
      });
    }
  }

  const states = [...byState.values()].sort((a, b) =>
    String(a.stateName).localeCompare(String(b.stateName))
  );
  usage[slug] = { stateCount: states.length, states };

  await updateDoc(ref, {
    usage,
    usedCount: Object.values(usage).filter((u) => u.stateCount > 0).length,
    lastAttachAt: new Date().toISOString(),
  });
}
